/**
 * Manager Tips Section Component
 * Handles garden tips / wellness tips management for managers
 */
import React from 'react';
import { Leaf, Heart, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

export function ManagerTipsSection({
    tips,
    onOpenNewModal,
    onEdit,
    onDelete,
    isResidential
}) {
    const buttonClass = isResidential ? 'bg-stone-600 hover:bg-stone-700' : 'bg-emerald-600 hover:bg-emerald-700';
    const TipIcon = isResidential ? Heart : Leaf;
    
    return (
        <div className="space-y-6" data-testid="manager-tips">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                    {isResidential ? 'Wellness Tips' : 'Garden Tips'}
                </h2>
                <Button onClick={onOpenNewModal} className={buttonClass} data-testid="manager-add-tip-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tip
                </Button>
            </div>

            <div className="text-sm text-muted-foreground">
                {tips.length} tips total
            </div>
            
            <div className="space-y-3">
                {tips.map(tip => (
                    <div key={tip.id} className="card-base" data-testid={`manager-tip-card-${tip.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex gap-4 flex-1">
                                {tip.image_url && (
                                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                        <img 
                                            src={tip.image_url} 
                                            alt={tip.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                            isResidential ? (
                                                tip.category === 'fitness' ? 'bg-orange-100 text-orange-700' :
                                                tip.category === 'mindfulness' ? 'bg-purple-100 text-purple-700' :
                                                tip.category === 'nutrition' ? 'bg-green-100 text-green-700' :
                                                tip.category === 'social' ? 'bg-blue-100 text-blue-700' :
                                                tip.category === 'health' ? 'bg-teal-100 text-teal-700' :
                                                'bg-gray-100 text-gray-600'
                                            ) : (
                                                tip.category === 'wildlife' ? 'bg-amber-100 text-amber-700' :
                                                tip.category === 'vegetables' ? 'bg-green-100 text-green-700' :
                                                tip.category === 'flowers' ? 'bg-pink-100 text-pink-700' :
                                                tip.category === 'seasonal' ? 'bg-orange-100 text-orange-700' :
                                                tip.category === 'plants' ? 'bg-lime-100 text-lime-700' :
                                                tip.category === 'indoor' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-600'
                                            )
                                        }`}>
                                            {tip.category || 'General'}
                                        </span>
                                        {tip.season && tip.season !== 'all_year' && (
                                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                                {tip.season.charAt(0).toUpperCase() + tip.season.slice(1)}
                                            </span>
                                        )}
                                        {!tip.is_active && (
                                            <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                                                Draft
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-bold text-lg">{tip.title}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-2">{tip.content}</p>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => onEdit(tip)}
                                    data-testid={`manager-edit-tip-${tip.id}`}
                                    title="Edit Tip"
                                >
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button 
                                    variant="destructive" 
                                    size="sm" 
                                    onClick={() => onDelete(tip)}
                                    data-testid={`manager-delete-tip-${tip.id}`}
                                    title="Delete Tip"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                
                {tips.length === 0 && (
                    <div className="card-base text-center py-12">
                        <TipIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No tips yet</p>
                        <Button onClick={onOpenNewModal} className={`mt-4 ${buttonClass}`}>
                            Create First Tip
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
